// Components/ChatComponents/BubbleManagement/BubbleParticipantsPanel.jsx
// FIXED v6: Prevents infinite re-renders + enforces participant_visibility

import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  TextField, InputAdornment, Menu, MenuItem, ListItemIcon,
  Alert, CircularProgress, Stack, Card, CardContent,
  Chip, Avatar,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Shield as ShieldIcon,
  Person as PersonIcon,
  Star as StarIcon,
  RemoveCircle as RemoveIcon,
  Groups as GroupsIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import API from '../API';

/* ───────────────────────── Styled Components ───────────────────────── */
const ParticipantCard = styled(Card)(({ theme, isHost, isAdmin }) => ({
  marginBottom: theme.spacing(1),
  border: isHost 
    ? `2px solid ${theme.palette.primary.main}`
    : isAdmin 
    ? `2px solid ${theme.palette.info.main}`
    : `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s',
  '&:hover': {
    boxShadow: theme.shadows[3],
    transform: 'translateX(4px)',
  },
}));

const RoleBadge = styled(Chip)(({ theme, role }) => {
  const colors = {
    host: { bg: theme.palette.primary.main, text: theme.palette.primary.contrastText },
    admin: { bg: theme.palette.info.main, text: theme.palette.info.contrastText },
    member: { bg: alpha(theme.palette.text.secondary, 0.1), text: theme.palette.text.secondary },
  };

  return {
    backgroundColor: colors[role]?.bg,
    color: colors[role]?.text,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: 20,
  };
});

/* ───────────────────────── Main Component ───────────────────────── */
export default function BubbleParticipantsPanel({
  open,
  onClose,
  bubble,
  conversationId,
  canManage,
}) {
  const queryClient = useQueryClient();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [snack, setSnack] = useState({ msg: '', sev: 'info' });

  // ✅ FIX: Extract primitive values to prevent infinite loops
  const bubbleId = bubble?.id;
  const hostGlobalUserId = bubble?.host_global_user_id;
  const adminIds = useMemo(() => bubble?.admin_ids || [], [bubble?.admin_ids?.join(',')]);
  const isHost = bubble?.is_host;

  // ✅ NEW: Check participant visibility permission
  const canViewParticipants = canManage || bubble?.participant_visibility === true;

  /* ───────────────────────── Data Fetching ───────────────────────── */
  
  const {
    data: participantsData,
    isLoading,
    error,
    refetch: refetchParticipants,
  } = useQuery(
    ["bubble-participants", bubbleId],
    () => API.bubbles.getConversationParticipants(bubbleId),
    {
      enabled: !!bubbleId && open && canViewParticipants,
      refetchOnWindowFocus: false,
    }
  );

  const participants = participantsData?.participants || [];

  /* ───────────────────────── Mutations ───────────────────────── */
  
  const removeMemberMutation = useMutation(
    (memberGlobalUserId) => API.bubbles.removeMember(bubbleId, memberGlobalUserId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bubble-metadata', bubbleId]);
        queryClient.invalidateQueries(['bubble-participants', bubbleId]);
        refetchParticipants();
        setSnack({ msg: 'Member removed successfully', sev: 'success' });
        handleMenuClose();
      },
      onError: (error) => {
        setSnack({ msg: error?.response?.data?.error || 'Failed to remove member', sev: 'error' });
      },
    }
  );

  const addAdminMutation = useMutation(
    (memberGlobalUserId) => API.bubbles.addAdmin(bubbleId, memberGlobalUserId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bubble-metadata', bubbleId]);
        queryClient.invalidateQueries(['bubble-participants', bubbleId]);
        refetchParticipants();
        setSnack({ msg: 'Admin added successfully', sev: 'success' });
        handleMenuClose();
      },
      onError: (error) => {
        setSnack({ msg: error?.response?.data?.error || 'Failed to add admin', sev: 'error' });
      },
    }
  );

  const removeAdminMutation = useMutation(
    (memberGlobalUserId) => API.bubbles.removeAdmin(bubbleId, memberGlobalUserId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['bubble-metadata', bubbleId]);
        queryClient.invalidateQueries(['bubble-participants', bubbleId]);
        refetchParticipants();
        setSnack({ msg: 'Admin privileges removed', sev: 'success' });
        handleMenuClose();
      },
      onError: (error) => {
        setSnack({ msg: error?.response?.data?.error || 'Failed to remove admin', sev: 'error' });
      },
    }
  );

  /* ───────────────────────── Handlers ───────────────────────── */
  const handleMenuOpen = useCallback((event, participant) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedParticipant(participant);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedParticipant(null);
  }, []);

  const handleRemoveMember = useCallback(() => {
    if (selectedParticipant?.global_user_id || selectedParticipant?.id) {
      const userId = selectedParticipant.global_user_id || selectedParticipant.id;
      removeMemberMutation.mutate(userId);
    }
  }, [selectedParticipant, removeMemberMutation]);

  const handleToggleAdmin = useCallback(() => {
    if (!selectedParticipant?.global_user_id && !selectedParticipant?.id) return;

    const userId = selectedParticipant.global_user_id || selectedParticipant.id;
    const isAdmin = adminIds.includes(userId);
    
    if (isAdmin) {
      removeAdminMutation.mutate(userId);
    } else {
      addAdminMutation.mutate(userId);
    }
  }, [selectedParticipant, adminIds, addAdminMutation, removeAdminMutation]);

  /* ───────────────────────── Participant Classification ───────────────────────── */
  const classifiedParticipants = useMemo(() => {
    if (!participants || participants.length === 0) {
      return { host: null, admins: [], members: [] };
    }

    const host = participants.find(p => 
      (p.global_user_id || p.id) === hostGlobalUserId
    );
    
    const admins = participants.filter(p => {
      const userId = p.global_user_id || p.id;
      return adminIds.includes(userId) && userId !== hostGlobalUserId;
    });
    
    const members = participants.filter(p => {
      const userId = p.global_user_id || p.id;
      return !adminIds.includes(userId) && userId !== hostGlobalUserId;
    });

    return { host, admins, members };
  }, [participants, hostGlobalUserId, adminIds]);

  const filteredParticipants = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return classifiedParticipants;

    const filterList = (list) => list.filter(p => {
      const name = p.display_name || p.name || p.username || '';
      const email = p.email || '';
      return name.toLowerCase().includes(query) || email.toLowerCase().includes(query);
    });

    return {
      host: classifiedParticipants.host && 
        (classifiedParticipants.host.display_name?.toLowerCase().includes(query) ||
         classifiedParticipants.host.email?.toLowerCase().includes(query))
        ? classifiedParticipants.host
        : null,
      admins: filterList(classifiedParticipants.admins),
      members: filterList(classifiedParticipants.members),
    };
  }, [classifiedParticipants, searchQuery]);

  const getParticipantRole = useCallback((participant) => {
    const userId = participant.global_user_id || participant.id;
    if (userId === hostGlobalUserId) return 'host';
    if (adminIds.includes(userId)) return 'admin';
    return 'member';
  }, [hostGlobalUserId, adminIds]);

  const renderParticipantCard = useCallback((participant) => {
    const role = getParticipantRole(participant);
    const isHostCard = role === 'host';
    const isAdminCard = role === 'admin';
    const userId = participant.global_user_id || participant.id;

    return (
      <ParticipantCard
        key={userId}
        isHost={isHostCard}
        isAdmin={isAdminCard}
        elevation={0}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={participant.pfphref || participant.avatar || participant.phphref}
              sx={{ width: 44, height: 44 }}
            >
              {(participant.display_name || participant.name || participant.username || 'U')[0].toUpperCase()}
            </Avatar>

            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                  {participant.display_name || participant.name || participant.username || 'Unknown'}
                </Typography>
                <RoleBadge
                  label={role.toUpperCase()}
                  role={role}
                  size="small"
                  icon={
                    isHostCard ? <StarIcon fontSize="small" /> :
                    isAdminCard ? <ShieldIcon fontSize="small" /> :
                    <PersonIcon fontSize="small" />
                  }
                />
              </Box>
              {participant.email && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {participant.email}
                </Typography>
              )}
            </Box>

            {canManage && !isHostCard && (
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, participant)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </ParticipantCard>
    );
  }, [getParticipantRole, canManage, handleMenuOpen]);

  /* ───────────────────────── Render - Access Denied State ───────────────────────── */
  // ✅ NEW: Show access denied if participant visibility is disabled
  if (!canViewParticipants) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { height: '60vh', borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Participants
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center" px={4}>
            <VisibilityOffIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Participant List Hidden
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              The host has disabled participant visibility for this bubble. Only admins can view the member list.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {bubble?.member_count || 0} total members
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  /* ───────────────────────── Main Render ───────────────────────── */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', borderRadius: 3 }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Participants
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {participantsData?.total || participants.length} total members
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Search Bar */}
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search participants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Error loading participants: {error?.response?.data?.error || error.message}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 2, overflow: 'auto', height: 'calc(100% - 80px)' }}>
            {/* Host Section */}
            {filteredParticipants.host && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  HOST
                </Typography>
                {renderParticipantCard(filteredParticipants.host)}
              </Box>
            )}

            {/* Admins Section */}
            {filteredParticipants.admins.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  ADMINS ({filteredParticipants.admins.length})
                </Typography>
                <Stack spacing={1}>
                  {filteredParticipants.admins.map(p => renderParticipantCard(p))}
                </Stack>
              </Box>
            )}

            {/* Members Section */}
            {filteredParticipants.members.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  MEMBERS ({filteredParticipants.members.length})
                </Typography>
                <Stack spacing={1}>
                  {filteredParticipants.members.map(p => renderParticipantCard(p))}
                </Stack>
              </Box>
            )}

            {/* Empty State */}
            {!filteredParticipants.host && 
             filteredParticipants.admins.length === 0 && 
             filteredParticipants.members.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <GroupsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {searchQuery ? 'No participants found' : 'No participants yet'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {isHost && selectedParticipant && (
          <MenuItem onClick={handleToggleAdmin}>
            <ListItemIcon>
              <ShieldIcon fontSize="small" />
            </ListItemIcon>
            <Typography>
              {adminIds.includes(selectedParticipant.global_user_id || selectedParticipant.id)
                ? 'Remove Admin'
                : 'Make Admin'}
            </Typography>
          </MenuItem>
        )}
        
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <RemoveIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography>Remove from Bubble</Typography>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      {snack.msg && (
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ msg: '', sev: 'info' })}
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
          }}
        >
          {snack.msg}
        </Alert>
      )}
    </Dialog>
  );
}